(() => {
  const TRANSITION_MS = 400;

  const initWellness = (root) => {
    if (root.dataset.gm3WellnessReady === 'true') return;
    root.dataset.gm3WellnessReady = 'true';

    const viewport = root.querySelector('[data-gm3-wellness-viewport]');
    const track = root.querySelector('[data-gm3-wellness-track]');
    const cards = Array.from(track ? track.querySelectorAll('[data-gm3-wellness-card]') : []);
    const prevBtn = root.querySelector('[data-gm3-wellness-prev]');
    const nextBtn = root.querySelector('[data-gm3-wellness-next]');
    const controls = root.querySelector('[data-gm3-wellness-controls]');
    const dotsWrap = root.querySelector('[data-gm3-wellness-dots]');

    if (!viewport || !track || cards.length === 0) return;
    if (!window.gsap || !window.Draggable) return;

    let activeIndex = 0;
    let draggableInstance = null;
    let dragStartX = 0;
    let dragStartTrackX = 0;

    const getCurrentX = () => {
      const val = window.gsap.getProperty(track, 'x');
      return typeof val === 'number' ? val : 0;
    };

    const getPadStart = () => {
      const styles = window.getComputedStyle(track);
      return parseFloat(styles.paddingInlineStart || styles.paddingLeft) || 0;
    };

    const getPadEnd = () => {
      const styles = window.getComputedStyle(track);
      return parseFloat(styles.paddingInlineEnd || styles.paddingRight) || 0;
    };

    const computeMaxX = () => {
      const padStart = getPadStart();
      const firstLeft = cards[0].getBoundingClientRect().left;
      const viewportLeft = viewport.getBoundingClientRect().left;
      return getCurrentX() + viewportLeft + padStart - firstLeft;
    };

    const computeMinX = () => {
      const padEnd = getPadEnd();
      const lastRight = cards[cards.length - 1].getBoundingClientRect().right;
      const viewportRight = viewport.getBoundingClientRect().right;
      return getCurrentX() + (viewportRight - padEnd) - lastRight;
    };

    const computeBounds = () => {
      const maxX = computeMaxX();
      const minX = computeMinX();
      return { minX: Math.min(minX, maxX), maxX: Math.max(minX, maxX) };
    };

    const normalizeWheelDelta = (event) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaX * 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaX * viewport.clientWidth;
      return event.deltaX;
    };

    const setArrowDisabled = (btn, disabled) => {
      if (!btn) return;
      btn.disabled = disabled;
      btn.setAttribute('aria-disabled', String(disabled));
    };

    const hasOverflow = () => track.scrollWidth > viewport.clientWidth + 1;

    const updateControlsVisibility = () => {
      const scrollable = hasOverflow();
      root.classList.toggle('gm3-wellness--no-overflow', !scrollable);
      if (controls) {
        controls.hidden = !scrollable;
        controls.setAttribute('aria-hidden', String(!scrollable));
      }
      if (!scrollable) {
        setArrowDisabled(prevBtn, true);
        setArrowDisabled(nextBtn, true);
      }
      return scrollable;
    };

    const clampToBounds = (x, bounds = computeBounds()) =>
      Math.max(bounds.minX, Math.min(bounds.maxX, x));

    const getCardTargetX = (card, bounds = computeBounds()) => {
      const padStart = getPadStart();
      const rawTargetX = -(card.offsetLeft - padStart);
      return clampToBounds(rawTargetX, bounds);
    };

    const getClosestSnapIndex = (x = getCurrentX()) => {
      const bounds = computeBounds();
      let closestIndex = activeIndex;
      let closestDistance = Infinity;

      cards.forEach((card, index) => {
        const targetX = getCardTargetX(card, bounds);
        const distance = Math.abs(targetX - x);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const getDirectionalSnapIndex = (direction, x = getCurrentX()) => {
      if (!direction) return getClosestSnapIndex(x);

      const bounds = computeBounds();
      const candidates = cards
        .map((card, index) => ({ index, targetX: getCardTargetX(card, bounds) }))
        .filter(({ targetX }) => (direction < 0 ? targetX <= x + 0.5 : targetX >= x - 0.5));

      if (candidates.length === 0) return getClosestSnapIndex(x);

      return candidates.reduce((closest, item) =>
        Math.abs(item.targetX - x) < Math.abs(closest.targetX - x) ? item : closest
      ).index;
    };

    const updateDots = () => {
      if (!dotsWrap) return;

      dotsWrap.querySelectorAll('[data-gm3-wellness-dot]').forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    const updatePositionState = (x = getCurrentX()) => {
      const bounds = computeBounds();
      activeIndex = getClosestSnapIndex(x);
      updateDots();
      if (!updateControlsVisibility()) return;
      setArrowDisabled(prevBtn, x >= bounds.maxX - 1);
      setArrowDisabled(nextBtn, x <= bounds.minX + 1);
    };

    const setActive = (index, animate = true) => {
      activeIndex = Math.max(0, Math.min(cards.length - 1, index));
      updateDots();

      if (!updateControlsVisibility()) {
        activeIndex = 0;
        updateDots();
        window.gsap.to(track, {
          x: 0,
          duration: 0,
          overwrite: true,
          onComplete: () => {
            draggableInstance?.[0]?.disable();
          },
        });
        return;
      }

      draggableInstance?.[0]?.enable();

      const padStart = getPadStart();
      const card = cards[activeIndex];
      const rawTargetX = getCurrentX()
        + viewport.getBoundingClientRect().left + padStart
        - card.getBoundingClientRect().left;

      const bounds = computeBounds();
      const targetX = clampToBounds(rawTargetX, bounds);

      setArrowDisabled(prevBtn, targetX >= bounds.maxX - 1);
      setArrowDisabled(nextBtn, targetX <= bounds.minX + 1);

      draggableInstance?.[0]?.disable();
      window.gsap.to(track, {
        x: targetX,
        duration: animate ? TRANSITION_MS / 1000 : 0,
        ease: 'power2.out',
        overwrite: true,
        onComplete: () => {
          draggableInstance?.[0]?.enable();
        },
      });
    };

    const moveBy = (direction) => {
      if (!direction) return;
      setActive(activeIndex + direction);
    };

    const renderDots = () => {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';

      cards.forEach((card, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'gm3-wellness__dot';
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        dot.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
        dot.setAttribute('data-gm3-wellness-dot', '');
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          setActive(index);
        });
        dotsWrap.appendChild(dot);
      });

      updateDots();
    };

    prevBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      if (prevBtn.disabled) return;
      moveBy(-1);
    });

    nextBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      if (nextBtn.disabled) return;
      moveBy(1);
    });

    viewport.addEventListener(
      'wheel',
      (event) => {
        if (cards.length < 2 || !hasOverflow()) return;

        const deltaX = normalizeWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();
        window.gsap.killTweensOf(track);
        window.gsap.set(track, { x: clampToBounds(getCurrentX() - deltaX) });
        updatePositionState();
      },
      { passive: false }
    );

    let dragBounds = null;

    draggableInstance = window.Draggable.create(track, {
      type: 'x',
      minimumMovement: 5,
      cursor: 'grab',
      activeCursor: 'grabbing',
      onDragStart() {
        if (!hasOverflow()) return;
        window.gsap.killTweensOf(track);
        dragBounds = computeBounds();
        dragStartX = this.startX;
        dragStartTrackX = getCurrentX();
      },
      onDrag() {
        if (!dragBounds) return;
        const x = getCurrentX();
        const limitedX = clampToBounds(x, dragBounds);
        if (limitedX !== x) window.gsap.set(track, { x: limitedX });
      },
      onDragEnd() {
        dragBounds = null;
        const trackDelta = getCurrentX() - dragStartTrackX;
        const pointerDelta = this.endX - dragStartX;
        const direction = Math.abs(trackDelta) > 1 ? Math.sign(trackDelta) : Math.sign(pointerDelta);
        setActive(getDirectionalSnapIndex(direction), true);
      },
    });

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => setActive(activeIndex, false), 150);
    });

    renderDots();
    setActive(0, false);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-gm3-wellness]').forEach(initWellness);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
